import {
  Box,
  Button,
  Container,
  Heading,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react"
import { Link, createFileRoute } from "@tanstack/react-router"
import type { ChangeEvent } from "react"
import { useEffect, useMemo, useState } from "react"

import type { QuizPublic } from "@/client"
import { QuizzesService } from "@/client"
import { useQuery } from "@tanstack/react-query"

import {
  type QuizExportData,
  downloadQuizDocx,
} from "@/components/Export/exportDocx"
import { toaster } from "@/components/ui/toaster"

export const Route = createFileRoute("/_layout/quiz-management/$quizId/export")(
  {
    component: QuizExport,
  },
)

function QuizExport() {
  const { quizId } = Route.useParams()
  const storageKey = `quizExport:${quizId}`

  const readPersistedOptions = () => {
    if (typeof window === "undefined") {
      return {
        includeAnswers: false,
        includeRationales: false,
        twoColumns: true,
      }
    }

    try {
      const raw = window.localStorage.getItem(storageKey)
      if (!raw) {
        return {
          includeAnswers: false,
          includeRationales: false,
          twoColumns: true,
        }
      }
      const parsed = JSON.parse(raw)
      return {
        includeAnswers: Boolean(parsed.includeAnswers),
        includeRationales: Boolean(parsed.includeRationales),
        twoColumns: parsed.twoColumns !== false,
      }
    } catch (error) {
      console.error("Failed to parse stored quiz export options", error)
      return {
        includeAnswers: false,
        includeRationales: false,
        twoColumns: true,
      }
    }
  }

  const persisted = readPersistedOptions()

  const {
    data: quiz,
    isLoading,
    error,
  } = useQuery<QuizPublic>({
    queryKey: ["quiz", quizId],
    queryFn: () => QuizzesService.getQuiz({ quizId }),
  })

  const [includeAnswers, setIncludeAnswers] = useState(persisted.includeAnswers)
  const [includeRationales, setIncludeRationales] = useState(
    persisted.includeRationales,
  )
  const [twoColumns, setTwoColumns] = useState(persisted.twoColumns)

  useEffect(() => {
    if (typeof window === "undefined") return
    const payload = {
      includeAnswers,
      includeRationales,
      twoColumns,
    }
    window.localStorage.setItem(storageKey, JSON.stringify(payload))
  }, [includeAnswers, includeRationales, twoColumns, storageKey])

  const printLink = useMemo(() => {
    const params = new URLSearchParams({
      answers: includeAnswers ? "1" : "0",
      rationales: includeRationales ? "1" : "0",
      columns: twoColumns ? "2" : "1",
    })
    return `/quiz-management/${quizId}/print?${params.toString()}`
  }, [includeAnswers, includeRationales, twoColumns, quizId])

  const disableDocx = isLoading || !quiz || (quiz.questions ?? []).length === 0

  if (isLoading) {
    return (
      <Container maxW="4xl" py={10} data-testid="quiz-export-page">
        <Stack align="center" py={20}>
          <Spinner size="lg" />
        </Stack>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxW="4xl" py={10} data-testid="quiz-export-page">
        <Box
          role="alert"
          borderWidth="1px"
          borderRadius="md"
          borderColor="red.200"
          bg="red.50"
          p={4}
        >
          <Text fontWeight="semibold" mb={1} color="red.700">
            Unable to load quiz
          </Text>
          <Text color="red.700">
            {(error as Error).message || "Please try again later."}
          </Text>
        </Box>
      </Container>
    )
  }

  if (!quiz) {
    return (
      <Container maxW="4xl" py={10} data-testid="quiz-export-page">
        <Box
          role="alert"
          borderWidth="1px"
          borderRadius="md"
          borderColor="yellow.200"
          bg="yellow.50"
          p={4}
          color="yellow.700"
        >
          <Text fontWeight="semibold">No quiz data available.</Text>
        </Box>
      </Container>
    )
  }

  return (
    <Container maxW="4xl" py={10} data-testid="quiz-export-page">
      <Stack gap={8}>
        <Stack gap={2}>
          <Heading size="lg">Export Quiz</Heading>
          <Text color="fg.muted">{quiz.name}</Text>
        </Stack>

        <Box>
          <Heading size="md" mb={4}>
            Export Options
          </Heading>
          <Stack gap={3} data-testid="quiz-export-options">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeAnswers}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setIncludeAnswers(event.target.checked)
                }
                disabled={isLoading}
                data-testid="quiz-export-answers-toggle"
              />
              <Text>Include answer key</Text>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeRationales}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setIncludeRationales(event.target.checked)
                }
                disabled={isLoading}
                data-testid="quiz-export-rationales-toggle"
              />
              <Text>Include rationales</Text>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={twoColumns}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setTwoColumns(event.target.checked)
                }
                disabled={isLoading}
                data-testid="quiz-export-columns-toggle"
              />
              <Text>Two column layout</Text>
            </label>
          </Stack>
        </Box>

        <Stack direction={{ base: "column", md: "row" }} gap={3}>
          <Link to={printLink} target="_blank">
            <Button
              as="span"
              colorPalette="blue"
              data-testid="quiz-export-open-print"
              disabled={isLoading}
            >
              Open Print View
            </Button>
          </Link>
          <Button
            variant="outline"
            disabled={disableDocx}
            onClick={async () => {
              if (!quiz) return
              try {
                await downloadQuizDocx(
                  mapQuizToExportData(quiz),
                  {
                    includeAnswers,
                    includeRationales,
                    twoColumns,
                  },
                  quiz.name,
                )
                toaster.create({
                  title: "Download started",
                  description: "Your DOCX download will begin shortly.",
                  type: "success",
                })
              } catch (error) {
                console.error(error)
                toaster.create({
                  title: "Download failed",
                  description:
                    error instanceof Error
                      ? error.message
                      : "Unable to generate DOCX",
                  type: "error",
                })
              }
            }}
            data-testid="quiz-export-download-docx"
          >
            Download DOCX
          </Button>
          <Button
            variant="outline"
            colorPalette="purple"
            disabled={isLoading || !quiz || (quiz.questions ?? []).length === 0}
            onClick={async () => {
              if (!quiz) return
              try {
                // Use fetch directly for binary data (axios corrupts binary responses)
                const token = localStorage.getItem("access_token")
                const apiBase = import.meta.env.VITE_API_URL || ""
                const response = await fetch(
                  `${apiBase}/api/v1/quizzes/${quizId}/export/powerpoint`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  },
                )

                if (!response.ok) {
                  throw new Error("Failed to export quiz")
                }

                const blob = await response.blob()
                const url = window.URL.createObjectURL(blob)
                const anchor = document.createElement("a")
                anchor.href = url
                anchor.download = `${quiz.name || "quiz"}.pptx`
                document.body.appendChild(anchor)
                anchor.click()
                document.body.removeChild(anchor)
                window.URL.revokeObjectURL(url)

                toaster.create({
                  title: "Download started",
                  description: "Your PowerPoint download will begin shortly.",
                  type: "success",
                })
              } catch (error) {
                console.error(error)
                toaster.create({
                  title: "Download failed",
                  description:
                    error instanceof Error
                      ? error.message
                      : "Unable to generate PowerPoint",
                  type: "error",
                })
              }
            }}
            data-testid="quiz-export-download-pptx"
          >
            Download PowerPoint
          </Button>
        </Stack>
      </Stack>
    </Container>
  )
}

function mapQuizToExportData(quiz: QuizPublic): QuizExportData {
  return {
    id: quiz.id,
    name: quiz.name,
    description: null,
    questions: (quiz.questions ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((question, index) => ({
        order: typeof question.order === "number" ? question.order : index + 1,
        questionText: question.question_text,
        options: question.options,
        correctAnswerIndex: question.correct_answer ?? null,
        rationale: question.rationale ?? null,
      })),
  }
}
